import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as BackArrowIcon } from '../../assets/icons/arrow-left.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon-red.svg';
import { ReactComponent as EditPencilCard } from '../../assets/icons/edit-pencil.svg';
import profiles from '../../assets/images/profiles/profile.jpg';
import { appCurrentBranding } from '../../config/branding';
import {
  AccountsInterFace,
  AccessUtilityPortal,
} from '../../interface/account-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { fetchAllAccounts } from '../../redux/slices/UtilityProviderManagementSlice';
import { changeDateFormat } from '../../shared-functions/DateTime';
import getFirstSubdomain from '../../shared-functions/DomainsplitFunction';
import account from '../../utils/api/account';
import authApi from '../../utils/api/auth';
import accountApi from '../../utils/api/utility-provider';
import MyButton from '../button/Button';
import CustomInput from '../custom-input/CustomInput';
import ConfirmBox from '../modal-box/ConfirmBox';
import ModalBox from '../modal-box/ModalBox';
import SuccessNotification from '../success-notification/SuccessNotification';

interface UtilityDetailsSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  isEditMode?: boolean;
  onEditModeChange?: (state: boolean) => void;
}

/**
 * Utility details side panel component
 * @param properties - The component properties
 * @returns The rendered side panel component
 */
function UtilityDetailsSidePanel(properties: UtilityDetailsSidePanelProperties) {
  const { t, i18n } = useTranslation('account');
  const dispatch = useAppDispatch();
  const { onEditModeChange, fetchIdFrom, id } = properties;
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { page, limit, accounts } = useAppSelector((state: any) => state.utility);

  const [accountData, setAccountData] = useState<AccountsInterFace | null>(null);
  // Modals state
  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    content: <div />,
    header: false,
    rightCorner: '',
    notification: false,
  });

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const subdomain = getFirstSubdomain();
  const shouldHideAccessUtility = Boolean(subdomain);
  const [confirmBox, setConfirmBox] = useState({
    status: false,
    title: '',
    subTitle: '',
    submitLabel: `${t('ok')}`,
    /**
     * Confirm submit function
     */
    conformSubmit: () => {},
  });

  /**
   * Show success notification
   * @param message - The success message to display
   */
  const showSuccessNotification = (message: string) => {
    setModalView({
      status: true,
      title: '',
      header: false,
      rightCorner: '',
      notification: true,
      content: (
        <SuccessNotification
          title={message}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={() => setModalView((previous) => ({ ...previous, status: false }))}
        />
      ),
    });
  };

  // Initial load
  useEffect(() => {
    let utilityId = id;
    if (fetchIdFrom === 'queryParams') {
      utilityId = searchParameters.get('utilityId') || undefined;
    }

    if (utilityId) {
      const found = accounts.find(
        (a: AccountsInterFace) => a.id === utilityId || a._id === utilityId,
      );
      if (found) {
        setAccountData(found);
      }
    }
  }, [fetchIdFrom, id, searchParameters, accounts]);

  /**
   * Refresh the accounts list
   */
  const refreshList = () => {
    dispatch(fetchAllAccounts({ page, limit }));
  };

  /**
   * Handle Reset Password
   */
  const handleResetPassword = () => {
    if (!accountData?.id) return; // or _id depending on API

    setConfirmBox({
      status: true,
      title: t('resetPasswordTitle'),
      subTitle: t('resetPasswordSubTitle'),
      submitLabel: t('proceed'),
      /**
       * Confirm submit function
       */
      conformSubmit: () => {
        // Placeholder
        setConfirmBox((previous) => ({ ...previous, status: false }));
        account
          .utilityRestPassword(accountData?.id, { submissionStatus: 'resetPassword' }) // send minimal or required data
          .then(() => {
            setConfirmBox((previous) => ({ ...previous, status: false }));
            setModalView((previous) => ({ ...previous, status: false }));
            showSuccessNotification(t('passwordResetSuccess'));
            dispatch(
              setNotificationMessage({
                message: t('passwordResetLinkSent'),
                status: true,
                type: 'success',
                code: 200,
              }),
            );
          })
          .catch((error: any) => {
            dispatch(
              setNotificationMessage({
                message: error?.response?.data?.message || t('passwordResetFailed'),
                status: true,
                type: 'error',
                code: error?.response?.data?.code,
              }),
            );
          });
      },
    });
  };

  /**
   * Toggle Edit Mode
   */
  const toggleEditMode = () => {
    if (editMode) {
      setEditMode(false);
      setEditData({});
      if (onEditModeChange) onEditModeChange(false);
    } else {
      setEditData({
        name: accountData?.name || '',
        email: accountData?.email || '',
        address: accountData?.address || '',
        // Utility might not have phone number in basic info shown in screenshot?
        // Screenshot shows: Email, Address, Created At.
        // List has Name.
      });
      setEditMode(true);
      if (onEditModeChange) onEditModeChange(true);
    }
  };

  /**
   * Handle Input Change
   */
  const handleInputChange = (name: string, value: string | number | any) => {
    setEditData((previous: any) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle Edit Submit
   */
  const handleEditSubmit = () => {
    if (!accountData?.id) return;

    accountApi
      .editUtilityAccounts(accountData.id, editData)
      .then(() => {
        showSuccessNotification(t('accountUpdatedSuccessfully'));
        setEditMode(false);
        if (onEditModeChange) onEditModeChange(false);
        refreshList();
      })
      .catch((error: any) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Close the side panel
   */
  const closeSidePanel = () => {
    searchParameters.delete('utilityId');
    setSearchParameters(searchParameters);
  };

  /**
   * View Utility Function
   */
  const viewUtilityFunction = (currentData: any) => {
    const accountDetails = accounts.find((value: AccountsInterFace) => value.id === currentData);

    if (accountDetails && Object.keys(accountDetails).length > 0) {
      const request: AccessUtilityPortal = {
        _id: accountDetails._id,
      };

      authApi
        .accessUtilityPortal(request)
        .then((response) => {
          const responseData = response?.data;
          const deployedType = window.origin.includes('test');
          window.open(
            `https://${appCurrentBranding}-utility${deployedType ? '-test' : ''}.web.app/login?accessToken=${responseData.access.token}&accessTokenExpires=${responseData.access.expires}&refreshToken=${responseData.refresh.token}&refreshTokenExpires=${responseData.refresh.expires}`,
            '_blank',
          );
        })
        .catch((error) => {
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * Handle Delete
   */
  const handleDelete = () => {
    if (!accountData?.id) return;
    setConfirmBox({
      status: true,
      title: t('deleteUtilityConfirmTitle'),
      subTitle: t('deleteUtilityConfirmSubtitle'),
      submitLabel: t('delete'),
      /**
       * Confirm submit function
       */
      conformSubmit: () => {
        accountApi
          .deleteAccount(accountData.id)
          .then(() => {
            setConfirmBox((previous) => ({ ...previous, status: false }));
            showSuccessNotification(t('utilityProviderAccountDeletedSuccessfully'));
            refreshList();
            closeSidePanel();
          })
          .catch((error: any) => {
            dispatch(
              setNotificationMessage({
                message: error?.response?.data?.message,
                status: true,
                type: 'error',
                code: 0,
              }),
            );
          });
      },
    });
  };

  if (!accountData) return <div className="no-data">{t('noDataFound')}</div>;

  return (
    <div className="utility-details-side-panel" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <ConfirmBox
        open={confirmBox.status}
        title={confirmBox.title}
        subTitle={confirmBox.subTitle}
        conformSubmit={confirmBox.conformSubmit}
        closeModal={() => setConfirmBox((previous) => ({ ...previous, status: false }))}
        submitLabel={confirmBox.submitLabel}
      />

      <div className="panel-header">
        {editMode && (
          <div className="edit-close">
            <BackArrowIcon onClick={toggleEditMode} />
          </div>
        )}
        <h3>{editMode ? t('editUtility') : t('utilityDetails')}</h3>
        <div className="header-actions">
          {!editMode && (
            <>
              <div className="text-primary" role="presentation" onClick={toggleEditMode}>
                <EditPencilCard />
              </div>
              <div className="text-danger" role="presentation" onClick={handleDelete}>
                <DeleteIcon />
              </div>
            </>
          )}
        </div>
      </div>

      <div
        style={{
          overflowY: 'scroll',
          height: window.innerHeight >= 700 ? 347 : 327,
        }}>
        <div className="utility-details-group">
          {!editMode && (
            <div className="profile-card">
              <div className="profile-info">
                <img src={profiles} alt="profile" />
                <div className="name">
                  <div>{accountData?.name || '-'}</div>
                </div>
              </div>
              {!shouldHideAccessUtility && (
                <div>
                  <MyButton
                    label={t('accessUtility')}
                    buttonType="primary"
                    onClickFunc={() => viewUtilityFunction(searchParameters.get('utilityId'))}
                  />
                </div>
              )}
            </div>
          )}

          <div className="section basic-info">
            <h4>{t('basicInformation')}</h4>
            {editMode ? (
              <>
                <div className="info-row-input">
                  <p className="label"> {t('userName')}:</p>
                  <CustomInput
                    type="text"
                    name="name"
                    placeholder={t('userName')}
                    value={editData.name}
                    onChangeValue={handleInputChange}
                  />
                </div>
                <div className="info-row-input">
                  <p className="label"> {t('email')}:</p>
                  <CustomInput
                    type="email"
                    name="email"
                    placeholder={t('email')}
                    value={editData.email}
                    onChangeValue={handleInputChange}
                  />
                </div>
                <div className="info-row-input">
                  <p className="label"> {t('address')}:</p>
                  <CustomInput
                    type="text"
                    name="address"
                    placeholder={t('address')}
                    value={editData.address}
                    onChangeValue={handleInputChange}
                  />
                </div>

                <div className="info-row">
                  <p className="label">{t('createdAt')}:</p>
                  <span>
                    {accountData?.createdAt ? changeDateFormat(accountData?.createdAt) : '-'}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="info-row">
                  <p className="label"> {t('email')}:</p>
                  <span>{accountData?.email || '-'}</span>
                </div>
                <div className="info-row">
                  <p className="label"> {t('address')}:</p>
                  <span>{accountData?.address || '-'}</span>
                </div>
                <div className="info-row">
                  <p className="label">{t('createdAt')}:</p>
                  <span>
                    {accountData?.createdAt ? changeDateFormat(accountData?.createdAt) : '-'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {editMode ? (
          <div className="footer-actions-edit">
            <MyButton label={t('save')} buttonType="submit" onClickFunc={handleEditSubmit} />
          </div>
        ) : (
          <div className="footer-reset">
            <MyButton
              label={t('resetPassword')}
              buttonType="outline"
              onClickFunc={handleResetPassword}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default UtilityDetailsSidePanel;
